import { ProtectedComponent } from '@components'

import { Module, Space, Spacing, useModal, withModalConditionalRender } from '@billy/design-system'
import { Button } from '@shinetools/lumen-react'
import { ChevronRight, FileSpreadsheet } from 'lucide-react'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { BankSearch } from '@features/bankAccounts/elements/ConnectBankContent/elements/BankSearch'
import { EditManualBankAccountModal } from '@features/bankAccounts/elements/EditManualBankAccountModal'
import { EditManualBankAccountModalState } from '@features/bankAccounts/elements/EditManualBankAccountModal/enums/editManualBankAccountModalState'
import { useRedirectToCsvImport } from '@features/bankAccounts/hooks/useRedirectToCsvImport'
import { MastercardTrustNote } from '@features/openBanking/elements/MastercardTrustNote'
import { PopularBanksTilesList } from '@features/openBanking/elements/PopularBanksTilesList'
import { usePopularBanksSearch } from '@features/openBanking/hooks/usePopularBanksSearch'
import { UiSource } from '@features/reconciliation/enums'

import { ModalId } from '../../../../../../enums/modalId'
import { Scope } from '../../../../../../enums/scope'
import { useComponentId, useSegment } from '../../../../../../hooks'
import * as Styled from './styles'

const POPULAR_BANKS_LIST_LENGTH = 8
const EditManualBankAccountModalRender = withModalConditionalRender(EditManualBankAccountModal)

export const OpenBankingConnectionModule = (): ReactElement => {
  const { t } = useTranslation()
  const editManualBankAccountModalId = useComponentId(ModalId.EditManualBankAccountModal)
  const { open: openModal } = useModal(editManualBankAccountModalId)
  const { redirectToCsvImport } = useRedirectToCsvImport()
  const { track } = useSegment()
  const { prefilledSearch, popularBanks, shouldShowPopularBanksList, handleSearchPrefill, handleClear } =
    usePopularBanksSearch(POPULAR_BANKS_LIST_LENGTH)

  const handleImportTransactions = useCallback(() => {
    track('Reconciliation CSV Account Created (Client)')
    openModal()
  }, [openModal, track])

  const handleCreateAccountSuccess = useCallback(
    (bankAccountId: string) => {
      redirectToCsvImport(bankAccountId)
    },
    [redirectToCsvImport],
  )

  return (
    <Module>
      <Module.Section title={t('bank.accounts.connection_module.title')} sideActions={<MastercardTrustNote />}>
        <Styled.SearchWrapper>
          <BankSearch
            prefilledSearch={prefilledSearch}
            onClear={handleClear}
            dropdownSize="xxxl"
            uiSource={UiSource.AccountPageBankLookup}
          />
        </Styled.SearchWrapper>
        <Space size="s24" />
        {shouldShowPopularBanksList && (
          <>
            <PopularBanksTilesList
              banks={popularBanks}
              onSearchPrefill={handleSearchPrefill}
              uiSource={UiSource.AccountPageBankLookup}
            />
            <Space size="s24" />
          </>
        )}
        <ProtectedComponent scopes={Scope.BankStatementImport}>
          <div>
            <Button
              variant="text"
              startIcon={<FileSpreadsheet />}
              endIcon={<ChevronRight />}
              label={t('bankreconciliation.connect_bank.import_csv')}
              style={{ fontWeight: 'var(--lumen-font-weight-regular)', marginLeft: `-${Spacing.S12}` }}
              onClick={handleImportTransactions}
            />
          </div>
        </ProtectedComponent>
      </Module.Section>

      <EditManualBankAccountModalRender
        id={editManualBankAccountModalId}
        accountId={undefined}
        bankAccountId={undefined}
        state={EditManualBankAccountModalState.CreateForCsvImport}
        onSuccess={handleCreateAccountSuccess}
      />
    </Module>
  )
}
